"use client";

// ============================================================
// CLUBHUB – Dokumente: was alle Teile der Ablage teilen
// ============================================================
// Baum, Inhalt, Seitenblatt und Dialoge brauchen denselben Bestand,
// dieselbe Adresse und dieselben Handgriffe (Dialog oeffnen,
// hochladen, loeschen). Die haelt `Ablage` und reicht sie hier
// herum, statt sie durch fuenf Ebenen Props zu fädeln.
// ============================================================

import { createContext, useContext } from "react";
import type { AblageDaten } from "@/lib/dokumente/views";
import type { Bestand } from "./ablage-zustand";
import type { Ansicht, useAdresse } from "./adresse";

export type DialogZiel =
  | { art: "ordner-neu"; parentId: string | null }
  | { art: "ordner-umbenennen"; folderId: string }
  | { art: "ordner-rechte"; folderId: string }
  | { art: "ordner-verschieben"; folderId: string }
  | { art: "dateien-verschieben"; dateiIds: string[] }
  | { art: "datei-umbenennen"; dateiId: string }
  | { art: "teilen"; dateiId: string }
  | { art: "hochladen-ziel"; dateien: File[] }
  | { art: "vorlage-verwenden"; vorlageId: string }
  | { art: "vorlage-bearbeiten"; vorlageId: string | null };

/** Zahl, Groesse und letzte Aenderung je Ordner — Unterordner eingerechnet. */
export type OrdnerZahl = { dateien: number; groesse: number; geaendert: string };

export type AblageKontextWert = {
  daten: AblageDaten;
  bestand: Bestand;
  aendern: (fn: (b: Bestand) => Bestand) => void;
  ansicht: Ansicht;
  setzen: ReturnType<typeof useAdresse>["setzen"];
  setzenHier: ReturnType<typeof useAdresse>["setzenHier"];
  /** Das Datei-Blatt schliessen — mit `history.back()`, wenn das Oeffnen einen Eintrag angelegt hat. */
  dateiSchliessen: () => void;
  oeffnen: (ziel: DialogZiel) => void;
  /** Ordnerwechsel: Suche, Art-Filter und offene Datei gehen; Sortierung und Darstellung bleiben. */
  zumOrdner: (folderId: string | null) => void;
  hochladen: (dateien: File[] | FileList, folderId: string | null) => void;
  neueVersion: (dateiId: string, datei: File) => void;
  dateienLoeschen: (ids: string[]) => Promise<void>;
  ordnerLoeschen: (folderId: string) => Promise<void>;
  herunterladen: (ids: string[]) => void;
  ordnerAlsZip: (folderId: string) => void;
  zahlen: Map<string, OrdnerZahl>;
};

const Kontext = createContext<AblageKontextWert | null>(null);

export const AblageKontext = Kontext.Provider;

export function useAblage(): AblageKontextWert {
  const wert = useContext(Kontext);
  if (!wert) throw new Error("useAblage ausserhalb der Ablage");
  return wert;
}
